<template>
  <v-sheet outlined rounded="lg" class="pa-4">
    <div class="d-flex justify-space-between align-center mb-3">
      <div class="text-subtitle-2 font-weight-bold">Типы услуг</div>
      <v-btn small text color="#483CB9" class="text-capitalize" @click="openCreate">
        <v-icon small left>mdi-plus</v-icon>
        Добавить
      </v-btn>
    </div>

    <v-btn-toggle v-model="activityFilter" mandatory dense class="calendar-activity-toggle mb-3">
      <v-btn small value="active">Активные</v-btn>
      <v-btn small value="inactive">Неактивные</v-btn>
      <v-btn small value="all">Все</v-btn>
    </v-btn-toggle>

    <div v-if="!serviceTypes.length" class="text-caption grey--text text-center py-4">
      Справочник пуст
    </div>
    <div v-else-if="!filteredServiceTypes.length" class="text-caption grey--text text-center py-4">
      Нет типов услуг, подходящих под выбранный фильтр
    </div>
    <v-expansion-panels v-else v-model="openPanels" multiple flat class="service-types">
      <v-expansion-panel
        v-for="item in filteredServiceTypes"
        :key="item.id"
        class="service-type"
        :class="{ 'service-type--inactive': item.is_active === false }"
      >
        <v-expansion-panel-header class="py-2 px-3">
          <div class="d-flex align-center min-width-0">
            <span class="text-caption grey--text mr-3 flex-shrink-0">{{ item.code }}</span>
            <span class="text-body-2 font-weight-medium text-truncate">{{ item.name }}</span>
            <v-chip v-if="item.is_active === false" x-small color="grey lighten-2" class="ml-2 flex-shrink-0">Неактивен</v-chip>
            <v-chip v-if="showLinks" x-small color="#ece9fb" class="ml-2 flex-shrink-0">
              <v-icon x-small left>mdi-link-variant</v-icon>
              {{ linksOf(item.id).length }}
            </v-chip>
          </div>
          <div class="d-flex align-center justify-end flex-grow-0 ml-2" @click.stop>
            <v-btn icon x-small title="Изменить тип услуги" @click="openEdit(item)">
              <v-icon small>mdi-pencil-outline</v-icon>
            </v-btn>
            <v-switch
              :input-value="item.is_active !== false"
              dense
              hide-details
              inset
              class="calendar-switch--small ma-0 pa-0"
              @change="onToggleActive(item)"
            />
          </div>
        </v-expansion-panel-header>

        <v-expansion-panel-content>
          <div v-if="!showLinks" class="text-caption grey--text">
            Управлять ссылками на запись могут администраторы и эксперты календаря.
          </div>
          <template v-else>
            <div class="d-flex align-center justify-space-between mb-2">
              <div class="text-caption grey--text text--darken-1">
                Гость записывается на этот тип услуги — ко всем экспертам или только к выбранным.
              </div>
              <v-btn
                v-if="canCreateLinkFor(item)"
                text
                small
                color="#483CB9"
                class="px-1 text-none flex-shrink-0"
                @click="$emit('create-link', { kind: 'service', mainId: item.id })"
              >
                <v-icon small left>mdi-link-plus</v-icon>
                Ссылка
              </v-btn>
            </div>
            <div v-if="!linksOf(item.id).length" class="text-caption grey--text">Ссылок пока нет</div>
            <PublicLinkItem
              v-for="link in linksOf(item.id)"
              :key="link.id"
              kind="service"
              :link="link"
              :experts="experts"
              :services="services"
              :service-types="serviceTypes"
              :can-manage="canManageLinkOf(item)"
              class="mb-2"
              @copy="$emit('copy-link', $event)"
              @edit="$emit('edit-link', $event)"
              @toggle-active="$emit('toggle-link-active', $event)"
            />
          </template>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-dialog v-model="dialog" max-width="360px">
      <v-card class="pa-2">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          {{ editing ? 'Редактирование типа услуги' : 'Новый тип услуги' }}
        </v-card-title>
        <v-card-text>
          <div class="text-caption grey--text text--darken-1 mb-1">Код</div>
          <v-text-field v-model="form.code" outlined dense hide-details class="mb-4" />
          <div class="text-caption grey--text text--darken-1 mb-1">Название</div>
          <v-text-field v-model="form.name" outlined dense hide-details />
        </v-card-text>
        <v-card-actions>
          <v-btn block color="#483CB9" :dark="Boolean(form.code && form.name)" :loading="loading" :disabled="!form.code || !form.name" @click="save">
            Сохранить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-sheet>
</template>

<script>
import PublicLinkItem from './PublicLinkItem.vue';

export default {
  name: 'ServiceTypesManager',
  components: { PublicLinkItem },
  props: {
    serviceTypes: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    serviceLinks: { type: Array, default: () => [] },
    experts: { type: Array, default: () => [] },
    services: { type: Array, default: () => [] },
    showLinks: { type: Boolean, default: false }, // false — нет прав на ссылки (403)
    // Типы услуг, ссылками на которые управляет пользователь; null — админ, все типы.
    linkServiceTypeIds: { type: Array, default: null },
  },
  data() {
    return {
      dialog: false,
      editing: null,
      form: { code: '', name: '' },
      activityFilter: 'active', // active | inactive | all
      openPanels: [],
    };
  },
  computed: {
    filteredServiceTypes() {
      return this.serviceTypes.filter((item) => {
        const isActive = item.is_active !== false;
        if (this.activityFilter === 'active') return isActive;
        if (this.activityFilter === 'inactive') return !isActive;
        return true;
      });
    },
  },
  watch: {
    // Раскрытые панели хранятся по индексу — при смене фильтра индексы съезжают.
    activityFilter() {
      this.openPanels = [];
    },
  },
  methods: {
    openCreate() {
      this.editing = null;
      this.form = { code: '', name: '' };
      this.dialog = true;
    },
    openEdit(item) {
      this.editing = item;
      this.form = { code: item.code, name: item.name };
      this.dialog = true;
    },
    save() {
      if (!this.form.code || !this.form.name) return;
      if (this.editing) {
        this.$emit('save', { id: this.editing.id, ...this.form });
      } else {
        this.$emit('save', { ...this.form });
      }
      this.dialog = false;
    },
    linksOf(serviceTypeId) {
      return this.serviceLinks.filter((link) => link.service_type === serviceTypeId);
    },
    canManageLinkOf(item) {
      return this.linkServiceTypeIds === null || this.linkServiceTypeIds.includes(item.id);
    },
    // Новую ссылку можно создать только на активный тип услуги.
    canCreateLinkFor(item) {
      return item.is_active !== false && this.canManageLinkOf(item);
    },
    onToggleActive(item) {
      this.$emit('toggle-active', { id: item.id, is_active: item.is_active === false });
    },
  },
};
</script>

<style lang="scss" scoped>
.calendar-switch--small {
  transform: scale(0.75);
  transform-origin: center;
}

.service-type {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px !important;
  margin-bottom: 8px;

  &::before {
    box-shadow: none;
  }

  &::after {
    display: none;
  }

  &--inactive .v-expansion-panel-header {
    opacity: 0.6;
  }
}

.min-width-0 {
  min-width: 0;
}

.calendar-activity-toggle .v-btn {
  text-transform: none;
}
</style>
